import {Autocomplete, TextInputWithTokens} from '@primer/react'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {keepPreviousData, useQuery} from '@github-ui/react-query'
import {useCallback, useDeferredValue, useMemo, useRef, useState} from 'react'
import type {KeyboardEventHandler} from 'react'

interface AutocompleteItem {
  id: string
  text: string
}

interface TopicTagInputProps {
  topics: string[]
  onChange: (topics: string[]) => void
  ownerLogin: string
  repoName: string
}

export function TopicTagInput({topics, onChange, ownerLogin, repoName}: TopicTagInputProps) {
  const [filterVal, setFilterVal] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const deferredQuery = useDeferredValue(filterVal.trim())

  const tokens: AutocompleteItem[] = useMemo(() => topics.map(t => ({id: t, text: t})), [topics])

  const {data: suggestions = []} = useQuery({
    queryKey: ['code-view-sidebar', 'topicAutocomplete', {ownerLogin, repoName, deferredQuery}],
    queryFn: async () => {
      if (!deferredQuery) return []
      const response = await verifiedFetchJSON(
        `/${ownerLogin}/${repoName}/topic_autocomplete?q=${encodeURIComponent(deferredQuery)}`,
      )
      if (!response.ok) return []
      const data = await response.json()
      const items: string[] = Array.isArray(data) ? data : (data.suggestions ?? [])
      return items
    },
    enabled: deferredQuery.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  })

  const items: AutocompleteItem[] = useMemo(() => suggestions.map(s => ({id: s, text: s})), [suggestions])

  const onTokenRemove = useCallback(
    (tokenId: string | number) => {
      onChange(topics.filter(t => t !== tokenId))
    },
    [topics, onChange],
  )

  const onSelectedChange = useCallback(
    (newSelectedItems: AutocompleteItem | AutocompleteItem[]) => {
      if (!Array.isArray(newSelectedItems)) return

      // Primer only tracks items in the current `items` array.
      // Merge with topics not currently visible in the menu.
      const menuItemIds = new Set(items.map(i => i.id))
      const topicsNotInMenu = topics.filter(t => !menuItemIds.has(t))
      const newMenuSelections = newSelectedItems.filter(Boolean).map(item => item.id)
      onChange([...topicsNotInMenu, ...newMenuSelections])
    },
    [onChange, topics, items],
  )

  const handleFilterChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFilterVal(e.currentTarget.value)
  }, [])

  const addFreeTopic = useCallback(
    (raw: string) => {
      const normalized = raw.trim().toLowerCase()
      if (normalized && !topics.includes(normalized)) {
        onChange([...topics, normalized])
      }
      setFilterVal('')
      if (inputRef.current) inputRef.current.value = ''
    },
    [topics, onChange],
  )

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = useCallback(
    e => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if ((e.key === ' ' || e.key === ',') && filterVal.trim()) {
        // Space or comma adds the typed text as a new topic
        e.preventDefault()
        addFreeTopic(filterVal)
        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      } else if (e.key === 'Enter' && filterVal.trim()) {
        const normalized = filterVal.trim().toLowerCase()
        if (!topics.includes(normalized) && items.length === 0) {
          e.preventDefault()
          addFreeTopic(filterVal)
        }
      }
    },
    [topics, items, filterVal, addFreeTopic],
  )

  return (
    <Autocomplete>
      <Autocomplete.Input
        ref={inputRef}
        as={TextInputWithTokens}
        tokens={tokens}
        onTokenRemove={onTokenRemove}
        onKeyDown={handleKeyDown}
        onChange={handleFilterChange}
        placeholder={topics.length === 0 ? 'Add topics' : ''}
        aria-label="Add topics"
        block
        size="medium"
      />
      <Autocomplete.Overlay>
        <Autocomplete.Menu
          items={items}
          selectedItemIds={topics}
          onSelectedChange={onSelectedChange}
          selectionVariant="multiple"
          aria-labelledby="topicAutocompleteLabel"
          emptyStateText={filterVal.trim() ? 'Press Enter to add as new topic' : 'Type to search topics'}
        />
      </Autocomplete.Overlay>
    </Autocomplete>
  )
}
